import { Node } from 'prosemirror-model'
import { type Command, TextSelection } from 'prosemirror-state'
import { RemoveMarkStep } from 'prosemirror-transform'

import { constants, schema } from '@showrunner/prose-schemas'

import { getConfigData } from '#choo-app/lib/editor/plugins/configData'
import { TrackedChangeStatus } from '#components/ChangeTracking/types'
import { getMarkExtent } from '#util/prosemirrorHelpers'

import { backspacePreservePageBreak, pageBreak } from './pageBreak'

export { backspacePreservePageBreak }

export { handleEnter } from './enter'

export const insertPageBreak: Command = (state, dispatch) => {
  const { paginationType } = getConfigData(state)
  if (paginationType === 'inline') {
    return pageBreak.create(state, dispatch)
  }
  return false
}

export const removePageBreak: Command = (state, dispatch) => {
  const { paginationType } = getConfigData(state)
  if (paginationType === 'inline') {
    return pageBreak.remove(state, dispatch)
  }
  return false
}

/**
 * Parameterized commands
 */

const { TRACKED_ADDITION, TRACKED_DELETION } = constants.MarkTypeMap

// mostly a ripoff of updateCommentMark
export const updateTrackedChangeMark = ({
  changeId,
  status,
  oldStatus,
  isAddition,
}: {
  changeId: string
  oldStatus: TrackedChangeStatus
  status: TrackedChangeStatus
  isAddition: boolean
}): Command => {
  const markType = isAddition ? TRACKED_ADDITION : TRACKED_DELETION

  return function (state, dispatch, editorView) {
    const { tr, doc } = state
    const oldValueMark = schema.marks[markType].create({
      id: changeId,
      status: oldStatus,
    })
    const removed = tr.removeMark(0, doc.content.size, oldValueMark)
    if (removed.steps.length) {
      removed.steps.forEach((step) => {
        const newMark = schema.marks[markType].create({
          id: changeId,
          status,
        })

        const removeStep = step as RemoveMarkStep
        tr.addMark(removeStep.from, removeStep.to, newMark)
        if (status === 'proposed') {
          editorView?.focus()
          tr.setSelection(
            TextSelection.create(tr.doc, removeStep.from),
          ).scrollIntoView()
        }
      })
      tr.setMeta('addToHistory', false)
      dispatch?.(tr)
      editorView?.focus()
    }
    return true
  }
}

const getProposedAdditionMarks = (node: Node, id: string) => {
  const candidate = schema.marks[TRACKED_ADDITION].create({ id })
  return node.marks.filter((m) => candidate.eq(m))
}

// to clean up a proposed deletion in the editor when the tracked change
// in the db is deleted, we simply need to remove the corresponding mark
export const removeProposedDeletionMark = (changeId: string): Command => {
  return function (state, dispatch) {
    const { tr, doc } = state
    const mark = schema.marks[TRACKED_DELETION].create({
      id: changeId,
    })
    tr.removeMark(0, doc.content.size, mark)
    tr.setMeta('addToHistory', false)
    dispatch?.(tr)
    return true
  }
}

// cleaning up a proposed addition is more involved.
// in addition to removing the mark, we also need to find and delete the proposed text
export const removeProposedAdditionMarkAndContent = (
  changeId: string,
): Command => {
  return function (state, dispatch) {
    const { tr } = state
    const ranges: { from: number; to: number }[] = []

    tr.doc.descendants((node, pos) => {
      const matches = getProposedAdditionMarks(node, changeId)
      matches.forEach((m) => {
        ranges.push(getMarkExtent(tr.doc.resolve(pos), m))
      })
    })

    ranges.forEach((m) => {
      const from = tr.mapping.map(m.from)
      const to = tr.mapping.map(m.to)
      tr.delete(from, to)
    })

    tr.setMeta('addToHistory', false)
    dispatch?.(tr)
    return true
  }
}
